import React, { useState, useEffect } from 'react';
import { X, Search, Phone, MessageSquare, Link } from 'lucide-react';

export default function InboxModal({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [matchingId, setMatchingId] = useState(null);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [searchingContacts, setSearchingContacts] = useState(false);
  const [matchedMsg, setMatchedMsg] = useState(null); // { name, reference }

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const token = localStorage.getItem('lf_token');
    fetch('/api/chat-ai/inbox/unmatched', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => setMessages(data.messages || []))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  }, [open]);

  const searchContacts = async (q) => {
    if (!q.trim()) { setContactResults([]); return; }
    setSearchingContacts(true);
    try {
      const token = localStorage.getItem('lf_token');
      const res = await fetch(`/api/records?recordType=contact&search=${encodeURIComponent(q)}&limit=8`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setContactResults(data.records || []);
    } catch(e) {}
    finally { setSearchingContacts(false); }
  };

  const matchToContact = async (commId, contactRecordId) => {
    try {
      const token = localStorage.getItem('lf_token');
      await fetch('/api/chat-ai/inbox/match', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ commId, recordId: contactRecordId }),
      });
      const matched = contactResults.find(c => (c.id || c.record_id) === contactRecordId);
      setMessages(prev => prev.filter(m => m.id !== commId));
      setMatchingId(null);
      setContactSearch('');
      setContactResults([]);
      setMatchedMsg({ name: matched?.title || 'Contact', reference: matched?.reference_number || '' });
    } catch(e) { console.error('[InboxModal] match error:', e); }
  };

  const filtered = search.trim()
    ? messages.filter(m =>
        (m.body || '').toLowerCase().includes(search.toLowerCase()) ||
        (m.from_address || '').includes(search)
      )
    : messages;

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl flex flex-col"
        style={{ width: '90vw', maxWidth: 680, maxHeight: '85vh' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-green-400" />
            <span className="text-sm font-semibold text-white">Unmatched Inbound SMS</span>
            {messages.length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: '#14532d', color: '#4ade80' }}>
                {messages.length} new
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300"><X className="h-4 w-4" /></button>
        </div>
        {/* Success toast */}
        {matchedMsg && (
          <div className="mx-5 mt-3 flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl text-sm font-medium"
            style={{ background: '#14532d', color: '#4ade80', border: '1px solid #166534' }}>
            <span>✅ SMS linked to {matchedMsg.name}{matchedMsg.reference ? ` (${matchedMsg.reference})` : ''} — now visible in their case comms.</span>
            <button onClick={() => setMatchedMsg(null)}
              className="flex-shrink-0 text-green-300 hover:text-white transition-colors text-lg leading-none">×</button>
          </div>
        )}
        <div className="px-5 py-3 border-b border-gray-800 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
            <input type="text" placeholder="Search messages or phone numbers..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-500" />
          </div>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-3">
          {loading ? (
            <p className="text-xs text-gray-500 text-center py-8">Loading...</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-8 w-8 text-gray-700 mx-auto mb-3" />
              <p className="text-sm text-gray-500">{search ? 'No results' : 'No unmatched messages'}</p>
            </div>
          ) : filtered.map(msg => (
            <div key={msg.id} className="bg-gray-800/60 rounded-xl border border-gray-700/50 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#14532d' }}>
                    <Phone className="h-4 w-4 text-green-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white">{msg.from_address}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: '#1f2937', color: '#9ca3af' }}>Unknown</span>
                    </div>
                    <span className="text-[11px] text-gray-500">
                      {msg.created_at ? new Date(msg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' · ' + new Date(msg.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => { setMatchingId(matchingId === msg.id ? null : msg.id); setContactSearch(''); setContactResults([]); }}
                  className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg font-semibold transition-colors"
                  style={{ background: '#1a3548', color: '#3ba3c9', border: '1px solid #1e4a6e' }}>
                  <Link className="h-3 w-3" />
                  Match to case
                </button>
              </div>
              <div className="px-4 pb-3">
                <p className="text-sm text-gray-200 bg-gray-900/60 rounded-lg px-3 py-2 border border-gray-700/40">
                  {msg.body || 'No content'}
                </p>
              </div>
              {matchingId === msg.id && (
                <div className="px-4 pb-4 border-t border-gray-700/50 pt-3">
                  <p className="text-xs text-gray-400 mb-2">Search for a contact to link this message to:</p>
                  <input type="text" placeholder="Type contact name..."
                    value={contactSearch}
                    onChange={e => { setContactSearch(e.target.value); searchContacts(e.target.value); }}
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-500 mb-2" />
                  {searchingContacts && <p className="text-xs text-gray-500">Searching...</p>}
                  {contactResults.length > 0 && (
                    <div className="space-y-1">
                      {contactResults.map(c => (
                        <button key={c.id || c.record_id} onClick={() => matchToContact(msg.id, c.id || c.record_id)}
                          className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-gray-700 transition-colors flex items-center justify-between"
                          style={{ background: '#111827', border: '1px solid #374151' }}>
                          <span className="text-gray-200 font-medium">{c.title}</span>
                          <span className="text-[10px] text-gray-500 font-mono">{c.reference_number}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
